/**
 * Phase 24.7 Step 5 — typed wrapper for /api/onboarding/submit.
 *
 * Mirrors backend OnboardingSubmitRequest / Response Pydantic schemas.
 * 409 on re-submit (Phase 24.7 Option A — re-onboarding deferred).
 */
import { api } from "./client";

export interface OnboardingSubmitRequest {
  // Bio
  name: string;
  age: number;
  weight_kg: number;
  height_cm: number;
  sex: "M" | "F";
  // Goal + Activity
  primary_goal:
    | "weight_loss"
    | "general_fitness"
    | "specific_event"
    | "health_maintenance";
  activity_level:
    | "sedentary"
    | "active"
    | "athlete_returning"
    | "athlete_continuous";
  // Experience
  has_endurance_experience?: boolean;
  pb_text?: string | null;
  injuries?: string | null;
  // Lifestyle
  weekly_hours: number;
  long_session_day: "sat" | "sun" | "either";
  preferred_training_time: "morning" | "midday" | "evening";
  // Discipline
  discipline_preference:
    | "running"
    | "cycling"
    | "swimming"
    | "triathlon"
    | "strength_cardio"
    | "unsure";
  // Race (conditional on primary_goal=specific_event)
  race_name?: string | null;
  race_date?: string | null; // ISO date
  race_type?: string | null;
  // Eating
  eating_pattern: "already_well" | "want_improve" | "no_idea";
  // Phase 24.7.3 T1B — dietary preferences (populate NutritionProfile).
  // dietary_style restricted to recipe-corpus-supported values; pescatarian/
  // flexitarian deferred to Sprint 24.8 (corpus expansion required).
  dietary_style?: "omnivore" | "vegetarian" | "vegan";
  allergies?: Array<
    "nuts" | "dairy" | "eggs" | "gluten" | "shellfish" | "soy" | "fish"
  >;
  religious_restrictions?: Array<
    "halal" | "kosher" | "lent" | "no_pork" | "no_beef" | "no_alcohol"
  >;
  preferred_cuisines?: Array<
    | "italian"
    | "asian"
    | "mediterranean"
    | "american"
    | "mexican"
    | "indian"
    | "french"
    | "russian"
    | "ukrainian"
  >;
  max_prep_time_min?: 15 | 30 | 45 | 60;
  // Zones
  defer_zones?: boolean;
  bike_ftp_watts?: number | null;
  swim_css_per_100m_sec?: number | null;
  run_threshold_pace_sec_per_km?: number | null;
  run_lthr_bpm?: number | null;
  // Notify
  notify_daily?: boolean;
  notify_weekly_review?: boolean;
}

export interface OnboardingSubmitResponse {
  user_id: number;
  onboarding_completed_at: string;
  profile_summary: Record<string, unknown>;
}

export async function submitOnboarding(
  payload: OnboardingSubmitRequest,
): Promise<OnboardingSubmitResponse> {
  const { data } = await api.post<OnboardingSubmitResponse>(
    "/onboarding/submit",
    payload,
  );
  return data;
}


// ===== Sprint S13.1.A (Day 8) — Phase E endpoint wrappers =====

import type { OnboardingData, TypicalSessionLength } from "@/stores/onboardingStore";


export interface OnboardingProfileResponse extends OnboardingData {
  // Read-back includes server-side timestamp set on first consent signing
  informed_consent_at: string | null;
}


/**
 * Phase E `GET /api/onboarding/profile`. Returns null if athlete_profile
 * не yet created (404 from server treated as legitimate "fresh" state).
 */
export async function getOnboardingProfile(): Promise<OnboardingProfileResponse | null> {
  try {
    const { data } = await api.get<OnboardingProfileResponse>(
      "/onboarding/profile",
    );
    return data;
  } catch (error) {
    // axios error shape — narrow к 404 only, propagate everything else
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
}


/**
 * Phase E `POST /api/onboarding/complete` — idempotent upsert. Each call
 * overwrites all 16 fields. `informed_consent_at` server timestamp set on
 * first signing.
 */
export async function completeOnboarding(
  payload: OnboardingData,
): Promise<{ status: string; user_id: number }> {
  const { data } = await api.post<{ status: string; user_id: number }>(
    "/onboarding/complete",
    payload,
  );
  return data;
}


// Re-export для convenience
export type { OnboardingData, TypicalSessionLength };
